import { Link, useNavigate, useParams } from 'react-router-dom';
import { useCodeLabel, useI18n, useLocalized } from '../lib/i18n';
import { formatDateTime, formatGel } from '../lib/format';
import { useVehicleHistory } from '../lib/queries';
import type { Inspection, VehicleHistory } from '../lib/types';
import { InspectionGallery } from '../components/inspection/InspectionGallery';
import { EraseButton } from '../components/EraseButton';
import { Alert, Amount, Card, EmptyState, Pill, SectionTitle, Skeleton } from '../components/ui';

type Entry =
  | { kind: 'inspection'; at: string; inspection: Inspection }
  | { kind: 'order'; at: string; order: VehicleHistory['orders'][number] };

/**
 * A car's whole story, newest first: every walk-round with its photos and
 * every visit it came in for. This is the page that settles "was that
 * scratch already there?".
 */
export function VehiclePage() {
  const { t } = useI18n();
  const localized = useLocalized();
  const code = useCodeLabel();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isPending, error } = useVehicleHistory(id);

  if (isPending) return <div className="mx-auto max-w-3xl space-y-3"><Skeleton className="h-24" /><Skeleton className="h-64" /></div>;
  if (error || !data) return <div className="mx-auto max-w-2xl"><Alert>{t('vehicle.notFound')}</Alert></div>;

  const { vehicle } = data;
  const entries: Entry[] = [
    ...data.inspections.map((inspection) => ({
      kind: 'inspection' as const, at: inspection.completedAt ?? inspection.createdAt, inspection,
    })),
    ...data.orders.map((order) => ({
      kind: 'order' as const, at: order.completedAt ?? order.acceptedAt ?? order.createdAt, order,
    })),
  ].sort((a, b) => b.at.localeCompare(a.at));

  const details = [vehicle.year, vehicle.color].filter(Boolean).join(' · ');

  return (
    <div className="mx-auto max-w-3xl pb-10">
      <Link to={`/customers/${vehicle.customerId}`} className="-my-2 inline-flex min-h-11 items-center text-xs text-ink-faint transition hover:text-ink-dim">
        ← {vehicle.customerName}
      </Link>

      <header className="mb-5 mt-3">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">{t('vehicle.history')}</p>
        <h1 className="mt-1.5 font-display text-[1.75rem] font-bold leading-tight tracking-tight text-ink sm:text-[2rem]">
          {vehicle.make} {vehicle.model}
        </h1>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-ink-dim">
          {vehicle.licensePlate && (
            <span className="tabular rounded-md border border-line px-1.5 py-0.5 text-xs text-ink">{vehicle.licensePlate}</span>
          )}
          {details && <span>{details}</span>}
          {vehicle.archivedAt && <Pill tone="warning">{t('vehicle.archived')}</Pill>}
        </div>
        {vehicle.vin && <p className="tabular mt-1 break-all text-xs text-ink-faint">VIN {vehicle.vin}</p>}
        <p className="mt-2 text-sm">
          <span className="text-ink-faint">{t('vehicle.owner')}: </span>
          <Link to={`/customers/${vehicle.customerId}`} className="text-ink transition hover:text-brand">
            {vehicle.customerName}
          </Link>
        </p>
        <EraseButton
          compact
          className="-ml-2 mt-1"
          path={`vehicles/${vehicle.id}`}
          name={[vehicle.make, vehicle.model, vehicle.licensePlate].filter(Boolean).join(' · ')}
          onDone={() => navigate(`/customers/${vehicle.customerId}`, { replace: true })}
        />
      </header>

      <SectionTitle>{t('vehicle.timeline')}</SectionTitle>
      {entries.length === 0 ? (
        <EmptyState title={t('inspection.none')} />
      ) : (
        <ol className="relative space-y-3 border-l border-line pl-4 sm:pl-5">
          {entries.map((entry) => (
            <li key={`${entry.kind}-${entry.kind === 'order' ? entry.order.id : entry.inspection.id}`} className="relative">
              <span
                aria-hidden
                className={`absolute -left-[1.3rem] top-5 h-2.5 w-2.5 rounded-full ring-4 ring-[var(--color-bg)] sm:-left-[1.55rem] ${
                  entry.kind === 'inspection' ? 'bg-brand' : 'bg-ink-faint'
                }`}
              />
              {entry.kind === 'inspection' ? (
                <Card><InspectionGallery inspection={entry.inspection} /></Card>
              ) : (
                <Link
                  to={`/orders/${entry.order.id}`}
                  className="block rounded-2xl border border-line bg-surface px-4 py-3 transition hover:border-brand/40 hover:bg-raised"
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="tabular text-sm font-semibold text-ink">{entry.order.reference}</span>
                    {entry.order.orderValueTetri > 0 && (
                      <Amount value={formatGel(entry.order.orderValueTetri)} className="shrink-0 text-sm text-ink" />
                    )}
                  </div>
                  <p className="mt-0.5 text-xs text-ink-faint">
                    {formatDateTime(entry.at)} · {code('status', entry.order.status)}
                  </p>
                  {entry.order.services.length > 0 && (
                    <p className="mt-1 text-sm text-ink-dim">{entry.order.services.map(localized).join(', ')}</p>
                  )}
                </Link>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
